import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Button, Divider, Empty, Image } from "antd";
import {
  CaretDownFilled,
  CaretUpFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { MainStore } from "core/store";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GOLFER_ROUTES } from "constants/routesPath";
import dayjs from "dayjs";
import SearchFilter from "./search-filter";
import CaddyCard from "./caddy-card";
import ComingSoon from "components/coming-soon";
import BGImage from "../../../assets/images/png/bg.png";
import api from "api";
import "./style.css";
import Loading from "components/loading";
import SearchTag from "./search-tag";

export default function SearchCaddy({ is_comming }) {
  const [filterList, setFilterList] = useState([]);
  const [filterParams, setFilterParams] = useState({});
  const [searchList, setSearchList] = useState([]);
  const [golfCourseList, setGolfCourseList] = useState([]);
  const [isSearch, setIsSearch] = useState(false);

  // Essential UI state only
  const [tfDateSearch, setTfDateSearch] = useState(null);
  const [price, setPrice] = useState(null);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);

  // Pagination and loading state
  const [page, setPage] = useState(1);
  const [noData, setNoData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingGolfCourses, setIsLoadingGolfCourses] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isApiCalling, setIsApiCalling] = useState(false);

  // Context and refs
  const { setUserData, userData } = useContext(MainStore);
  const [pathParams, setPathParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchFilter, setSearchFilter] = useState({
    ...Object.fromEntries(pathParams),
  });
  const formSearchRef = useRef();

  // Initialize data on component mount
  useEffect(() => {
    getGolfCourse();
  }, []);

  // Handle pagination
  useEffect(() => {
    if (!noData && page > 1 && isInitialized && !isApiCalling) {
      GetCaddies();
    }
  }, [page]);

  useEffect(() => {
    GetCaddies(true);
  }, [searchFilter]);

  useEffect(() => {
    filterParamsUrl();
  }, [golfCourseList]);

  useEffect(() => {
    formSearchRef.current?.setFieldsValue({});
  }, []);

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight } = document.documentElement;
    const { innerHeight } = window;
    const scrolledToBottom = Math.ceil(scrollTop + innerHeight) >= scrollHeight;

    if (
      scrolledToBottom &&
      !loading &&
      !noData &&
      !loadingMore &&
      !isApiCalling &&
      isInitialized
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [loading, noData, loadingMore, isApiCalling, isInitialized]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const filterParamsUrl = (page) => {
    const urlParams = {
      golfCourseId: searchFilter?.golfCourseId,
      search: searchFilter?.search,
      date: searchFilter?.date
        ? dayjs(
            searchFilter?.date,
            searchFilter?.date.split("-")[0].length === 4
              ? "YYYY-MM-DD"
              : "DD-MM-YYYY"
          ).format("DD-MM-YYYY")
        : "",
      time: searchFilter?.time,
      priceRange: {
        minPrice: searchFilter?.minPrice,
        maxPrice: searchFilter?.maxPrice,
      },
      modePreference: searchFilter?.modePreference,
    };

    const params = {};
    Object.keys(urlParams).forEach((key) => {
      const value = urlParams[key];
      if (value) {
        params[key] = value;
      }
    });

    if (page) params.page = page;

    const filterList = [];

    if (urlParams.golfCourseId) {
      const foundGolfCourse = golfCourseList?.find(
        (course) => course.id === urlParams.golfCourseId
      );
      formSearchRef.current?.setFieldValue(
        "golfCourse",
        urlParams.golfCourseId
      );
      if (foundGolfCourse) {
        filterList.push({ type: "GolfCouese", name: foundGolfCourse.name });
      }
    }
    if (urlParams.search) {
      formSearchRef.current?.setFieldValue("caddyName", urlParams.search);
      filterList.push({
        type: "search",
        name: `ค้นหา: ${urlParams.search}`,
      });
    }

    if (urlParams.date) {
      const dateFormatted = dayjs(urlParams.date, "DD-MM-YYYY");

      setTfDateSearch(dateFormatted.format("YYYY-MM-DD"));
      formSearchRef.current?.setFieldValue("teeOffDate", dateFormatted);
      filterList.push({
        type: "date",
        name: dateFormatted.format("DD-MMMM-YYYY"),
      });

      if (urlParams.time) {
        const timeFormatted = dayjs(urlParams.time, "HH:mm");
        formSearchRef.current?.setFieldValue("teeOffTime", timeFormatted);
        filterList.push({
          type: "time",
          name: urlParams.time + " น.",
        });
      }
    }
    const { minPrice, maxPrice } = urlParams.priceRange;
    if (minPrice && maxPrice) {
      setPrice([minPrice, maxPrice]);
      filterList.push({
        type: "price",
        name: `ราคา: ${minPrice}-${maxPrice} บาท`,
      });
      formSearchRef.current?.setFieldValue("priceRange", [minPrice, maxPrice]);
    }

    if (urlParams.modePreference) {
      formSearchRef.current?.setFieldValue(
        "modePreference",
        urlParams.modePreference
      );
      const modeLabel =
        urlParams.modePreference === "walking"
          ? "เดิน"
          : urlParams.modePreference === "both"
          ? "รถกอล์ฟหรือเดิน"
          : "รถกอล์ฟ";
      filterList.push({
        type: "modePreference",
        name: `ใช้รถหรือเดิน: ${modeLabel}`,
      });
    }

    setFilterList(filterList);
    return params;
  };

  const getGolfCourse = async () => {
    if (isApiCalling) return;
    setIsLoadingGolfCourses(true);
    try {
      const { data, status } =
        await api.masterDataApi.getMasterDataGolfCourses();

      if (status !== 200) return;

      const { data: golfCourse, price } = data;
      setMaxPrice(price.max);
      setMinPrice(price.min);
      formSearchRef.current?.setFieldValue("priceRange", [
        price.min,
        price.max,
      ]);
      setGolfCourseList(golfCourse);
    } catch (error) {
      console.error("Golf course fetch error:", error);
    } finally {
      setIsLoadingGolfCourses(false);
      setIsInitialized(true);
    }
  };

  const onClickAddFavorite = useCallback(
    async (id) => {
      const updatedUserData = { ...userData };
      if (!updatedUserData.favorite) {
        updatedUserData.favorite = [];
      }
      updatedUserData.favorite.push(id);

      try {
        const { status } = await api.favoriteApi.updateFavorite(userData.id, {
          favorite: updatedUserData.favorite,
        });
        if (status === 200) setUserData(updatedUserData);
      } catch (error) {
        console.error("Add favorite error:", error);
      }
    },
    [userData, setUserData]
  );

  const onClickRemoveFavorite = useCallback(
    async (id) => {
      const updatedUserData = { ...userData };
      if (!updatedUserData.favorite) {
        updatedUserData.favorite = [];
      }

      const favoriteIndex = updatedUserData.favorite.findIndex(
        (fav) => parseInt(fav) === parseInt(id)
      );

      if (favoriteIndex !== -1) {
        updatedUserData.favorite.splice(favoriteIndex, 1);
      }

      try {
        const { status } = await api.favoriteApi.updateFavorite(userData.id, {
          favorite: updatedUserData.favorite,
        });
        if (status === 200) setUserData(updatedUserData);
      } catch (error) {
        console.error("Remove favorite error:", error);
      }
    },
    [userData, setUserData]
  );

  const onRemoveFilter = useCallback(
    (index, type) => {
      const updatedFilters = [...filterList];
      updatedFilters.splice(index, 1);
      setFilterList(updatedFilters);

      const newParams = pathParams;

      setPage(1);

      const filterActions = {
        date: () => {
          setSearchFilter((prev) => ({ ...prev, date: null, time: null }));
          setTfDateSearch(null);
          newParams.delete("date");
          newParams.delete("time");

          formSearchRef.current?.setFieldsValue({
            teeOffDate: undefined,
            teeOffTime: undefined,
          });
        },
        time: () => {
          setSearchFilter((prev) => ({ ...prev, time: null }));
          newParams.delete("time");
          formSearchRef.current?.setFieldsValue({
            teeOffTime: undefined,
          });
        },
        price: () => {
          const defaultPriceRange = [minPrice, maxPrice];
          setPrice(defaultPriceRange);
          setSearchFilter((prev) => ({
            ...prev,
            minPrice: null,
            maxPrice: null,
          }));
          newParams.delete("minPrice");
          newParams.delete("maxPrice");
          formSearchRef.current?.setFieldsValue({
            priceRange: defaultPriceRange,
          });
        },
        GolfCouese: () => {
          setSearchFilter((prev) => ({ ...prev, golfCourseId: null }));
          newParams.delete("golfCourseId");
          formSearchRef.current?.setFieldsValue({
            golfCourse: undefined,
          });
        },
        search: () => {
          setSearchFilter((prev) => ({ ...prev, search: null }));
          newParams.delete("search");
          formSearchRef.current?.setFieldsValue({
            caddyName: undefined,
          });
        },
        modePreference: () => {
          setSearchFilter((prev) => ({ ...prev, modePreference: null }));
          newParams.delete("modePreference");
          formSearchRef.current?.setFieldsValue({
            modePreference: undefined,
          });
        },
      };

      if (filterActions[type]) {
        filterActions[type]();
      }

      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${newParams.toString()}`
      );
    },
    [filterList, pathParams, formSearchRef, minPrice, maxPrice]
  );

  const GetCaddies = async (isReset) => {
    if (isApiCalling && !isReset && isInitialized) return;

    setLoadingMore(true);
    setIsApiCalling(true);

    if (isReset) {
      setPage(1);
      setSearchList([]);
      setLoading(false);
      setNoData(false);
    }
    const searchParams = filterParamsUrl(page);

    try {
      const { data } = await api.caddyApi.getAllCaddies(searchParams);
      const caddies = data.data;

      if (caddies.length === 0) {
        setNoData(true);
        setLoading(false);
        return;
      }

      if (isReset || page === 1) {
        setSearchList(caddies);
      } else {
        setSearchList((prev) => {
          const existingIds = new Set(prev.map((caddy) => caddy.id));
          const newCaddies = caddies.filter(
            (caddy) => !existingIds.has(caddy.id)
          );
          return [...prev, ...newCaddies];
        });
      }
    } catch (error) {
      console.error("Caddy search error:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setIsApiCalling(false);
    }
  };
  return !is_comming ? (
    <ComingSoon />
  ) : (
    <div>
      <Loading visible={loading} />
      <div className="search-caddy-container">
        <div
          style={{
            padding: 25,
            fontWeight: "bold",
            fontSize: 24,
            position: "sticky",
            top: 0,
            left: 0,
            zIndex: 9,
            background: "white",
            boxShadow:
              "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
            borderBottomRightRadius: 30,
            borderBottomLeftRadius: 30,
          }}
        >
          <center>SEARCH CADDY</center>
        </div>
        <div className="search-caddy-content">
          <div
            style={{
              background: "white",
              padding: 20,
              borderRadius: 20,
              boxShadow:
                "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
            }}
          >
            <h3 style={{ marginBottom: 0 }}>ค้นหา Caddy</h3>
            <Button
              type="primary"
              size="large"
              block
              style={{
                height: "auto",
                minHeight: 44,
                whiteSpace: "normal",
                padding: "8px 16px",
                margin: "12px 0 4px",
              }}
              onClick={() => navigate(GOLFER_ROUTES.TIP_OFFER_LIST)}
            >
              ไม่เจอแคดดี้ที่ใช่? จองแคดดี้ให้ฉัน (Book me a caddie)
            </Button>

            <div>
              <SearchFilter
                price={price}
                setPrice={setPrice}
                setPage={setPage}
                isSearch={isSearch}
                formSearchRef={formSearchRef}
                setFilterList={setFilterList}
                GetCaddies={GetCaddies}
                setIsSearch={setIsSearch}
                golfCourseList={golfCourseList}
                tfDateSearch={tfDateSearch}
                setTfDateSearch={setTfDateSearch}
                minPrice={minPrice}
                maxPrice={maxPrice}
                isLoadingGolfCourses={isLoadingGolfCourses}
                setPathParams={setPathParams}
                setSearchFilter={setSearchFilter}
                searchFilter={searchFilter}
              />
            </div>
          </div>

          <div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {filterList.map((tag, index) => (
                <SearchTag
                  key={`tag_${tag.type}_${index}`}
                  tag_name={tag.name}
                  index={index}
                  type={tag.type}
                  onRemoveFilter={() => onRemoveFilter(index, tag.type)}
                />
              ))}
            </div>
          </div>
          <div>
            <b style={{ fontSize: 18, marginTop: 10 }}>รายการแคดดี้</b>
            <Divider style={{ margin: "10px 0px 0px 0px" }} />
          </div>
          <div>
            {searchList?.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "49% 49%",
                  gap: 10,
                  animation: "fadeIn 0.5s ease-in-out",
                }}
              >
                {searchList?.map((caddy, index) => {
                  const isfav = userData?.favorite?.find(
                    (fav) => parseInt(fav) === parseInt(caddy.id)
                  );
                  return (
                    <div
                      key={`caddy_card_${caddy.id}_${index}`}
                      style={{
                        animation: `slideInUp 0.5s ease-out ${
                          index * 0.01
                        }s both`,
                        opacity: 0,
                      }}
                    >
                      <CaddyCard
                        data={caddy}
                        index={index}
                        isfav={isfav}
                        dateSearch={searchFilter?.date}
                        golfCourseSearch={searchFilter?.golfCourseId}
                        teeOffTimeSearch={searchFilter?.time}
                        modePreferenceSearch={searchFilter?.modePreference}
                        onClickAddFavorite={onClickAddFavorite}
                        onClickRemoveFavorite={onClickRemoveFavorite}
                      />
                    </div>
                  );
                })}
              </div>
            ) : loading ? (
              <div
                style={{
                  background: "white",
                  padding: 20,
                  borderRadius: 20,
                  boxShadow:
                    "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 200,
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "pulse 0.2s ease-in-out infinite",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <LoadingOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                  <div style={{ color: "#059669", fontSize: 16 }}>
                    กำลังค้นหาแคดดี้
                    <span className="loading-dots">
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  background: "white",
                  padding: 10,
                  borderRadius: 20,
                  boxShadow:
                    "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 200,
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "fadeIn 0.5s ease-in-out",
                }}
              >
                {loadingMore ? (
                  <Empty
                    description={
                      <span
                        style={{
                          color: "#065f46",
                          fontSize: 16,
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        กำลังค้นหาแคดดี้
                        <span className="loading-dots">
                          <span>.</span>
                          <span>.</span>
                          <span>.</span>
                        </span>
                      </span>
                    }
                    image={
                      <div
                        style={{
                          color: "#065f46",
                          scale: "1.5",
                          filter:
                            "hue-rotate(200deg) saturate(0.5) brightness(0.8)",
                        }}
                      >
                        {Empty.PRESENTED_IMAGE_SIMPLE}
                      </div>
                    }
                  />
                ) : (
                  <Empty
                    description={
                      <span style={{ color: "#999", fontSize: 16 }}>
                        ไม่พบแคดดี้
                      </span>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </div>
            )}
          </div>

          {loadingMore && searchList?.length > 0 && (
            <center>
              <div
                style={{
                  background: "white",
                  borderRadius: 15,
                  padding: 20,
                  margin: "20px 0",
                  boxShadow: "rgba(0, 0, 0, 0.08) 0px 2px 8px",
                  animation: "fadeIn 0.5s ease-in-out",
                }}
              >
                <h3
                  style={{
                    color: "rgba(0, 0, 0, 0.65)",
                    display: "flex",
                    gap: 15,
                    alignItems: "center",
                    justifyContent: "center",
                    margin: 0,
                    fontSize: 16,
                    fontWeight: 500,
                  }}
                >
                  <LoadingOutlined
                    style={{
                      fontSize: 20,
                      color: "#1890ff",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  <div>กำลังโหลดข้อมูลเพิ่มเติม...</div>
                </h3>
              </div>
            </center>
          )}
        </div>

        <div
          style={{
            position: "fixed",
            zIndex: 0,
            bottom: 0,
            left: 0,
            opacity: 0.5,
          }}
        >
          <Image
            width="100%"
            height={150}
            preview={false}
            src={BGImage}
            style={{
              filter:
                "brightness(0) saturate(100%) invert(77%) sepia(67%) saturate(395%) hue-rotate(87deg) brightness(91%) contrast(85%)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
